import { ElementData, CombinationResult } from "./types";

// HoloLab Design System Colors
export const HOLOLAB_COLORS = {
  // Primary Palette
  labWhite: '#FFFFFF',
  deepBlue: '#0A1929',
  precisionBlue: '#2E5C8A',
  
  // Extended Palette
  iceBlue: '#E8F4F8',
  indigo: '#1E3A5F',
  
  // Accent Colors
  successGreen: '#10B981',
  warningAmber: '#F59E0B',
  errorRed: '#EF4444',
  infoBlue: '#3B82F6',
  
  // UI Opacity Variants
  labWhite5: 'rgba(255, 255, 255, 0.05)',
  labWhite10: 'rgba(255, 255, 255, 0.1)',
  labWhite40: 'rgba(255, 255, 255, 0.4)',
  labWhite60: 'rgba(255, 255, 255, 0.6)',
  deepBlue08: 'rgba(10, 25, 41, 0.08)',
  deepBlue10: 'rgba(10, 25, 41, 0.1)',
  deepBlue20: 'rgba(10, 25, 41, 0.2)',
  deepBlue40: 'rgba(10, 25, 41, 0.4)',
  deepBlue50: 'rgba(10, 25, 41, 0.5)',
  deepBlue60: 'rgba(10, 25, 41, 0.6)',
  deepBlue80: 'rgba(10, 25, 41, 0.8)',
  indigo20: 'rgba(30, 58, 95, 0.2)',
  precisionBlue20: 'rgba(46, 92, 138, 0.2)',
  precisionBlue50: 'rgba(46, 92, 138, 0.5)',
};

// HoloLab Typography
export const HOLOLAB_FONTS = {
  heading: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  body: "'Inter', sans-serif",
  mono: "'JetBrains Mono', 'Courier New', monospace",
  data: "'JetBrains Mono', 'Courier New', monospace",
};

// HoloLab Spacing (8px grid system)
export const HOLOLAB_SPACING = {
  xs: '4px',
  sm: '8px',
  md: '12px',
  lg: '16px',
  xl: '24px',
  '2xl': '32px',
  '3xl': '48px',
  '4xl': '64px',
};

export const ELEMENTS: ElementData[] = [
  {
    symbol: "H",
    name: "Hydrogen",
    color: "#00FFFF",
    atomicNumber: 1,
    description: "Lightest element",
  },
  {
    symbol: "O",
    name: "Oxygen",
    color: "#FF4444",
    atomicNumber: 8,
    description: "Life supporter",
  },
  {
    symbol: "Na",
    name: "Sodium",
    color: "#FFFF00",
    atomicNumber: 11,
    description: "Reactive metal",
  },
  {
    symbol: "Cl",
    name: "Chlorine",
    color: "#00FF00",
    atomicNumber: 17,
    description: "Toxic gas",
  },
  {
    symbol: "C",
    name: "Carbon",
    color: "#AAAAAA",
    atomicNumber: 6,
    description: "Life basis",
  },
  {
    symbol: "Fe",
    name: "Iron",
    color: "#FF8800",
    atomicNumber: 26,
    description: "Metal",
  },
  {
    symbol: "N",
    name: "Nitrogen",
    color: "#5500FF",
    atomicNumber: 7,
    description: "Atmosphere gas",
  },
  {
    symbol: "S",
    name: "Sulfur",
    color: "#FFDD00",
    atomicNumber: 16,
    description: "Yellow crystal",
  },
  {
    symbol: "Ca",
    name: "Calcium",
    color: "#EEEEEE",
    atomicNumber: 20,
    description: "Bone mineral",
  },
  {
    symbol: "Ho",
    name: "Holmium",
    color: "#FF6B9D",
    atomicNumber: 67,
    description: "Rare earth metal",
  },
];

export const COMBINATIONS: CombinationResult[] = [
  {
    elements: ["H", "O"],
    result: {
      symbol: "H2O",
      name: "Water",
      color: "#4488FF",
      atomicNumber: 0,
      description: "Essential for life",
    },
    requiredCatalyst: "heat",
  },
  {
    elements: ["Na", "Cl"],
    result: {
      symbol: "NaCl",
      name: "Salt",
      color: "#FFFFFF",
      atomicNumber: 0,
      description: "Table salt",
    },
  },
  // Sodium + Water (Safe without heat)
  {
    elements: ["Na", "H2O"],
    result: {
      symbol: "NaOH",
      name: "Sodium Hydroxide",
      color: "#DDDDDD",
      atomicNumber: 0,
      description: "Lye",
    },
  },
  {
    elements: ["H", "Cl"],
    result: {
      symbol: "HCl",
      name: "Hydrochloric Acid",
      color: "#CC00FF",
      atomicNumber: 0,
      description: "Strong acid",
    },
  },
  {
    elements: ["C", "O"],
    result: {
      symbol: "CO2",
      name: "Carbon Dioxide",
      color: "#888888",
      atomicNumber: 0,
      description: "Greenhouse gas",
    },
    requiredCatalyst: "heat",
  },
  {
    elements: ["N", "H"],
    result: {
      symbol: "NH3",
      name: "Ammonia",
      color: "#0000FF",
      atomicNumber: 0,
      description: "Cleaner",
    },
    requiredCatalyst: "chemical",
  },
  {
    elements: ["Fe", "O"],
    result: {
      symbol: "Fe2O3",
      name: "Iron Oxide",
      color: "#8B0000",
      atomicNumber: 0,
      description: "Rust",
    },
  },
  {
    elements: ["Ca", "Cl"],
    result: {
      symbol: "CaCl2",
      name: "Calcium Chloride",
      color: "#DDDDDD",
      atomicNumber: 0,
      description: "De-icing salt",
    },
  },
  {
    elements: ["N", "O"],
    result: {
      symbol: "NO2",
      name: "Nitrogen Dioxide",
      color: "#993300",
      atomicNumber: 0,
      description: "Pollutant",
    },
    requiredCatalyst: "light",
  },
  // Additional Base Element Combinations
  {
    elements: ["S", "O"],
    result: {
      symbol: "SO2",
      name: "Sulfur Dioxide",
      color: "#FFAA00",
      atomicNumber: 0,
      description: "Acid rain component",
    },
    requiredCatalyst: "heat",
  },
  {
    elements: ["C", "H"],
    result: {
      symbol: "CH4",
      name: "Methane",
      color: "#88CC88",
      atomicNumber: 0,
      description: "Natural gas",
    },
    requiredCatalyst: "heat",
  },
  {
    elements: ["S", "H"],
    result: {
      symbol: "H2S",
      name: "Hydrogen Sulfide",
      color: "#AA8800",
      atomicNumber: 0,
      description: "Rotten egg gas",
    },
  },
  // Compound-to-Compound Combinations
  {
    elements: ["H2O", "CO2"],
    result: {
      symbol: "H2CO3",
      name: "Carbonic Acid",
      color: "#4488AA",
      atomicNumber: 0,
      description: "Soda water",
    },
  },
  {
    elements: ["HCl", "NaOH"],
    result: {
      symbol: "NaCl",
      name: "Salt",
      color: "#FFFFFF",
      atomicNumber: 0,
      description: "Neutralization reaction",
    },
  },
  {
    elements: ["NH3", "HCl"],
    result: {
      symbol: "NH4Cl",
      name: "Ammonium Chloride",
      color: "#CCCCCC",
      atomicNumber: 0,
      description: "Smoke",
    },
  },
  // === CALCIUM-BASED REACTIONS ===
  {
    elements: ["Ca", "O"],
    result: {
      symbol: "CaO",
      name: "Calcium Oxide",
      color: "#F5F5DC",
      atomicNumber: 0,
      description: "Quicklime",
    },
    requiredCatalyst: "heat",
  },
  {
    elements: ["Ca", "H2O"],
    result: {
      symbol: "Ca(OH)2",
      name: "Calcium Hydroxide",
      color: "#FFFAF0",
      atomicNumber: 0,
      description: "Slaked lime",
    },
  },
  {
    elements: ["CaO", "H2O"],
    result: {
      symbol: "Ca(OH)2",
      name: "Calcium Hydroxide",
      color: "#FFFAF0",
      atomicNumber: 0,
      description: "Slaking reaction",
    },
  },
  {
    elements: ["Ca", "S"],
    result: {
      symbol: "CaS",
      name: "Calcium Sulfide",
      color: "#F0E68C",
      atomicNumber: 0,
      description: "Oldhamite",
    },
    requiredCatalyst: "heat",
  },
  {
    elements: ["Ca", "N"],
    result: {
      symbol: "Ca3N2",
      name: "Calcium Nitride",
      color: "#8B7355",
      atomicNumber: 0,
      description: "Brown powder",
    },
    requiredCatalyst: "heat",
  },
  // === IRON-BASED REACTIONS ===
  {
    elements: ["Fe", "S"],
    result: {
      symbol: "FeS",
      name: "Iron Sulfide",
      color: "#696969",
      atomicNumber: 0,
      description: "Pyrrhotite",
    },
    requiredCatalyst: "heat",
  },
  {
    elements: ["Fe", "S"],
    result: {
      symbol: "FeS2",
      name: "Iron Disulfide",
      color: "#FFD700",
      atomicNumber: 0,
      description: "Fool's Gold",
    },
    requiredCatalyst: "pressure",
  },
  {
    elements: ["Fe", "Cl"],
    result: {
      symbol: "FeCl3",
      name: "Iron Chloride",
      color: "#8B4513",
      atomicNumber: 0,
      description: "Ferric chloride",
    },
    requiredCatalyst: "chemical",
  },
  {
    elements: ["Fe", "C"],
    result: {
      symbol: "Fe3C",
      name: "Cementite",
      color: "#708090",
      atomicNumber: 0,
      description: "Steel component",
    },
    requiredCatalyst: "pressure",
  },
  // === CARBON TRANSFORMATIONS ===
  {
    elements: ["C", "C"],
    result: {
      symbol: "C(d)",
      name: "Diamond",
      color: "#B9F2FF",
      atomicNumber: 0,
      description: "Crystalline carbon",
    },
    requiredCatalyst: "pressure",
  },
  {
    elements: ["C", "C"],
    result: {
      symbol: "C(g)",
      name: "Graphite",
      color: "#2F4F4F",
      atomicNumber: 0,
      description: "Layered carbon",
    },
    requiredCatalyst: "heat",
  },
  {
    elements: ["C", "O"],
    result: {
      symbol: "CO",
      name: "Carbon Monoxide",
      color: "#CD5C5C",
      atomicNumber: 0,
      description: "Toxic gas",
    },
  },
  {
    elements: ["C", "S"],
    result: {
      symbol: "CS2",
      name: "Carbon Disulfide",
      color: "#FFE4B5",
      atomicNumber: 0,
      description: "Volatile liquid",
    },
    requiredCatalyst: "heat",
  },
  // === SODIUM-BASED REACTIONS ===
  {
    elements: ["Na", "O"],
    result: {
      symbol: "Na2O",
      name: "Sodium Oxide",
      color: "#FFFACD",
      atomicNumber: 0,
      description: "White solid",
    },
    requiredCatalyst: "heat",
  },
  {
    elements: ["Na", "H"],
    result: {
      symbol: "NaH",
      name: "Sodium Hydride",
      color: "#E0E0E0",
      atomicNumber: 0,
      description: "Strong base",
    },
    requiredCatalyst: "heat",
  },
  {
    elements: ["Na", "S"],
    result: {
      symbol: "Na2S",
      name: "Sodium Sulfide",
      color: "#FFF8DC",
      atomicNumber: 0,
      description: "Yellow crystals",
    },
    requiredCatalyst: "heat",
  },
  // === SULFUR OXIDE SERIES ===
  {
    elements: ["SO2", "O"],
    result: {
      symbol: "SO3",
      name: "Sulfur Trioxide",
      color: "#FF6347",
      atomicNumber: 0,
      description: "Corrosive gas",
    },
    requiredCatalyst: "chemical",
  },
  {
    elements: ["S", "O"],
    result: {
      symbol: "SO3",
      name: "Sulfur Trioxide",
      color: "#FF6347",
      atomicNumber: 0,
      description: "Complete oxidation",
    },
    requiredCatalyst: "chemical",
  },
  // === NITROGEN OXIDE SERIES ===
  {
    elements: ["N", "O"],
    result: {
      symbol: "NO",
      name: "Nitric Oxide",
      color: "#87CEEB",
      atomicNumber: 0,
      description: "Colorless gas",
    },
    requiredCatalyst: "heat",
  },
  {
    elements: ["N", "O"],
    result: {
      symbol: "N2O",
      name: "Nitrous Oxide",
      color: "#B0E0E6",
      atomicNumber: 0,
      description: "Laughing gas",
    },
    requiredCatalyst: "chemical",
  },
  {
    elements: ["NO", "O"],
    result: {
      symbol: "NO2",
      name: "Nitrogen Dioxide",
      color: "#993300",
      atomicNumber: 0,
      description: "Brown toxic gas",
    },
  },
  // === ACID FORMATION ===
  {
    elements: ["SO3", "H2O"],
    result: {
      symbol: "H2SO4",
      name: "Sulfuric Acid",
      color: "#FFE5B4",
      atomicNumber: 0,
      description: "King of chemicals",
    },
  },
  {
    elements: ["H", "SO3"],
    result: {
      symbol: "H2SO4",
      name: "Sulfuric Acid",
      color: "#FFE5B4",
      atomicNumber: 0,
      description: "Strong acid",
    },
  },
  {
    elements: ["NO2", "H2O"],
    result: {
      symbol: "HNO3",
      name: "Nitric Acid",
      color: "#FFDAB9",
      atomicNumber: 0,
      description: "Corrosive acid",
    },
  },
  {
    elements: ["N", "H2O"],
    result: {
      symbol: "HNO3",
      name: "Nitric Acid",
      color: "#FFDAB9",
      atomicNumber: 0,
      description: "Oxidizing acid",
    },
    requiredCatalyst: "chemical",
  },
  // === CARBONATE FORMATION ===
  {
    elements: ["Ca", "CO2"],
    result: {
      symbol: "CaCO3",
      name: "Calcium Carbonate",
      color: "#F0F8FF",
      atomicNumber: 0,
      description: "Limestone",
    },
    requiredCatalyst: "pressure",
  },
  {
    elements: ["CaO", "CO2"],
    result: {
      symbol: "CaCO3",
      name: "Calcium Carbonate",
      color: "#F0F8FF",
      atomicNumber: 0,
      description: "Lime carbonation",
    },
  },
  // === REDUCTION REACTIONS ===
  {
    elements: ["Fe2O3", "C"],
    result: {
      symbol: "Fe",
      name: "Iron",
      color: "#FF8800",
      atomicNumber: 26,
      description: "Smelted iron",
    },
    requiredCatalyst: "heat",
  },
  {
    elements: ["Fe2O3", "CO"],
    result: {
      symbol: "Fe",
      name: "Iron",
      color: "#FF8800",
      atomicNumber: 26,
      description: "Blast furnace",
    },
    requiredCatalyst: "heat",
  },
  // === AMMONIA REACTIONS ===
  {
    elements: ["NH3", "H2O"],
    result: {
      symbol: "NH4OH",
      name: "Ammonium Hydroxide",
      color: "#E6E6FA",
      atomicNumber: 0,
      description: "Aqueous ammonia",
    },
  },
  {
    elements: ["NH3", "O"],
    result: {
      symbol: "NO",
      name: "Nitric Oxide",
      color: "#87CEEB",
      atomicNumber: 0,
      description: "Ostwald process",
    },
    requiredCatalyst: "chemical",
  },
  // === BLEACH & SANITIZERS ===
  {
    elements: ["NaOH", "Cl"],
    result: {
      symbol: "NaClO",
      name: "Sodium Hypochlorite",
      color: "#F0FFFF",
      atomicNumber: 0,
      description: "Bleach",
    },
    requiredCatalyst: "chemical",
  },
  {
    elements: ["Ca", "Cl"],
    result: {
      symbol: "Ca(ClO)2",
      name: "Calcium Hypochlorite",
      color: "#F5F5F5",
      atomicNumber: 0,
      description: "Pool chlorine",
    },
    requiredCatalyst: "chemical",
  },
  // === COMBUSTION REACTIONS ===
  {
    elements: ["CH4", "O"],
    result: {
      symbol: "CO2",
      name: "Carbon Dioxide",
      color: "#888888",
      atomicNumber: 0,
      description: "Methane combustion",
    },
    requiredCatalyst: "heat",
  },
  {
    elements: ["C2H5OH", "O"],
    result: {
      symbol: "CO2",
      name: "Carbon Dioxide",
      color: "#888888",
      atomicNumber: 0,
      description: "Ethanol combustion",
    },
    requiredCatalyst: "heat",
  },
  // === ORGANIC COMPOUNDS ===
  {
    elements: ["C", "H"],
    result: {
      symbol: "C2H5OH",
      name: "Ethanol",
      color: "#E0E0E0",
      atomicNumber: 0,
      description: "Drinking alcohol",
    },
    requiredCatalyst: "chemical",
  },
  {
    elements: ["C2H5OH", "O"],
    result: {
      symbol: "CH3COOH",
      name: "Acetic Acid",
      color: "#FFFACD",
      atomicNumber: 0,
      description: "Vinegar",
    },
    requiredCatalyst: "chemical",
  },
  {
    elements: ["C", "H2O"],
    result: {
      symbol: "C6H12O6",
      name: "Glucose",
      color: "#FFF8DC",
      atomicNumber: 0,
      description: "Simple sugar",
    },
    requiredCatalyst: "light",
  },
  {
    elements: ["C6H12O6", "C6H12O6"],
    result: {
      symbol: "C12H22O11",
      name: "Sucrose",
      color: "#FFFAF0",
      atomicNumber: 0,
      description: "Table sugar",
    },
    requiredCatalyst: "chemical",
  },
  // === PEROXIDE FORMATION ===
  {
    elements: ["H2O", "O"],
    result: {
      symbol: "H2O2",
      name: "Hydrogen Peroxide",
      color: "#E0FFFF",
      atomicNumber: 0,
      description: "Bleaching agent",
    },
    requiredCatalyst: "chemical",
  },
  {
    elements: ["H", "O"],
    result: {
      symbol: "H2O2",
      name: "Hydrogen Peroxide",
      color: "#E0FFFF",
      atomicNumber: 0,
      description: "Oxidizer",
    },
    requiredCatalyst: "chemical",
  },
  // === OZONE FORMATION ===
  {
    elements: ["O", "O"],
    result: {
      symbol: "O3",
      name: "Ozone",
      color: "#87CEFA",
      atomicNumber: 0,
      description: "Protective layer",
    },
    requiredCatalyst: "light",
  },
  // === CYANOGEN & CYANIDES ===
  {
    elements: ["C", "N"],
    result: {
      symbol: "C2N2",
      name: "Cyanogen",
      color: "#D3D3D3",
      atomicNumber: 0,
      description: "Toxic gas",
    },
    requiredCatalyst: "heat",
  },
  {
    elements: ["Na", "C2N2"],
    result: {
      symbol: "NaCN",
      name: "Sodium Cyanide",
      color: "#F5F5F5",
      atomicNumber: 0,
      description: "Highly toxic",
    },
    requiredCatalyst: "chemical",
  },
  // === PRESSURE TRANSFORMATIONS ===
  {
    elements: ["H2O", "H2O"],
    result: {
      symbol: "Ice-VII",
      name: "Exotic Ice",
      color: "#B0E0E6",
      atomicNumber: 0,
      description: "High-pressure ice",
    },
    requiredCatalyst: "pressure",
  },
  {
    elements: ["CO2", "CO2"],
    result: {
      symbol: "CO2(s)",
      name: "Dry Ice",
      color: "#F0F8FF",
      atomicNumber: 0,
      description: "Solid CO2",
    },
    requiredCatalyst: "pressure",
  },
  {
    elements: ["N", "H"],
    result: {
      symbol: "NH3",
      name: "Ammonia",
      color: "#0000FF",
      atomicNumber: 0,
      description: "Haber process",
    },
    requiredCatalyst: "pressure",
  },
  // === SALT FORMATIONS ===
  {
    elements: ["NaCl", "H2SO4"],
    result: {
      symbol: "Na2SO4",
      name: "Sodium Sulfate",
      color: "#FFFFFF",
      atomicNumber: 0,
      description: "Glauber's salt",
    },
    requiredCatalyst: "heat",
  },
  {
    elements: ["Ca(OH)2", "HCl"],
    result: {
      symbol: "CaCl2",
      name: "Calcium Chloride",
      color: "#DDDDDD",
      atomicNumber: 0,
      description: "Neutralization",
    },
  },
  // === ADVANCED COMPOUNDS ===
  {
    elements: ["Fe", "H2O"],
    result: {
      symbol: "Fe(OH)2",
      name: "Iron Hydroxide",
      color: "#90EE90",
      atomicNumber: 0,
      description: "Green rust",
    },
  },
  {
    elements: ["S", "Cl"],
    result: {
      symbol: "S2Cl2",
      name: "Sulfur Dichloride",
      color: "#FFD700",
      atomicNumber: 0,
      description: "Amber liquid",
    },
    requiredCatalyst: "chemical",
  },
  {
    elements: ["N", "Cl"],
    result: {
      symbol: "NCl3",
      name: "Nitrogen Trichloride",
      color: "#FFFF99",
      atomicNumber: 0,
      description: "Explosive oil",
    },
    requiredCatalyst: "chemical",
  },
  {
    elements: ["C", "Cl"],
    result: {
      symbol: "CCl4",
      name: "Carbon Tetrachloride",
      color: "#D3D3D3",
      atomicNumber: 0,
      description: "Toxic solvent",
    },
    requiredCatalyst: "chemical",
  },
];

export const GESTURE_COOLDOWN = 1000; // ms
